import React, { useEffect, useState } from "react";
import { Box, Button, Checkbox, CircularProgress, IconButton, InputAdornment, makeStyles, MenuItem, Select, Typography } from "@material-ui/core";
import { guestRegisterFormDefaults, guestRegisterFormSchema, registerFormPasswordDefaults, registerFormPasswordSchema } from "@constants/auth";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import { InputField } from "../../shared/components/Form";
import classes from "@css/components/Register.module.css";
import Modal from "../../shared/components/Modal";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import AddressField from "../../shared/components/AddressField";
import { smsAlerts } from "../../constants/booking";
import { Link } from "react-router-dom";
import { registerWithEmail } from "../../store/actions/auth";
import { findExistingCustomer, validatePhone } from "../../store/actions/users";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { roles } from "../../constants/roles";
import { formatEmail } from "../../shared/utils/formatters";
import { segmentTrack } from "../../helpers/segment";
import AccountExistsModal from "./AccountExistsModal";
import { validateCompanyName } from "../../store/actions/businessAccounts";
import useDebounce from "../../shared/hooks/useDebounce";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(0, 2),
    gap: theme.spacing(1),
  },
  registerForm: {
    margin: "20px 0 40px 0",
  },
  label: {
    fontWeight: 600,
  },
  columnContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  margin2: {
    margin: theme.spacing(2, 0),
  },
  rowContainer: {
    display: "flex",
    gap: theme.spacing(1),
  },
  addressContainer: {
    display: "grid",
    gridTemplateColumns: "auto 150px",
    gap: theme.spacing(1),
  },
  alerts: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
  },
  linkOuter: {
    color: "background: rgba(106, 111, 115, 1)",
    font: "13px",
    fontWeight: 500,
    padding: theme.spacing(2, 0),
  },
  link: {
    color: "inherit",
    textDecoration: "underline",
    cursor: "pointer",
  },
  button: {
    marginTop: theme.spacing(2),
  },
  containerClass: {
    maxWidth: "400px",
    padding: theme.spacing(3, 0),
  },
  errorText: {
    fontSize: "12px",
    color: theme.palette.error.main,
  },
}));

const ModalContent = ({ setOpen, isValidUser, setIsValidUser, setAccountExistsModal, acceptTask }) => {
  const compClasses = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const { id: bookingId } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const queryUserType = queryParams.get("userType");
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [isValidCompanyName, setIsValidCompanyName] = useState(true);
  const userType = useSelector((state) => state.tempData.userType);

  const {
    control: registerControl,
    watch,
    handleSubmit: registerSubmit,
    errors: registerErrors,
  } = useForm({
    defaultValues: guestRegisterFormDefaults(),
    resolver: yupResolver(guestRegisterFormSchema),
  });

  const {
    control: registerPasswordControl,
    errors: registerPasswordErrors,
    handleSubmit: registerPasswordSubmit,
  } = useForm({
    defaultValues: registerFormPasswordDefaults(),
    resolver: yupResolver(registerFormPasswordSchema),
  });

  const inputProps = {
    control: registerControl,
    errors: registerErrors,
    className: classes.textField,
  };

  const inputPasswordProps = {
    control: registerPasswordControl,
    errors: registerPasswordErrors,
    className: classes.textField,
  };

  useEffect(() => {
    if (acceptTask) {
      localStorage.setItem("accept_task_id", bookingId);
    }
  }, [acceptTask]);

  useEffect(() => {
    if (queryUserType !== null) dispatch({ type: "SET_TEMP_USER", userType: queryUserType });
  }, [queryUserType]);

  const trackProviderAction = (action, payload) => {
    if (userType === roles.provider.value) {
      segmentTrack(action, payload);
    }
  };

  const registerClient = async (data) => {
    trackProviderAction("Warrior Clicks Submit - Register", { fullName: data.fullName, email: formatEmail(data.email) });
    const user = {
      fullName: data.fullName,
      companyName: data.companyName,
      email: formatEmail(data.email),
      password: data.password,
      phone: data.phone,
      userType,
      smsSettings: { address: data.address, distanceRange: data.distanceRange, taskAvailable: smsAlerts.defaults.taskAvailable },
      alerts: data?.alerts ? true : false,
      isAbbreviated: true,
    };
    await dispatch(registerWithEmail({ newUser: user }));
    setIsLoading(false);
    return;
  };

  const onRegisterSubmit = async (data) => {
    if (Object.keys(inputProps.errors).length || Object.keys(inputPasswordProps.errors).length || !isValidCompanyName) return;
    if (!showPasswordFields && isValidUser.show) {
      setAccountExistsModal(true);
      setOpen(false);
    } else if (!showPasswordFields) {
      setShowPasswordFields(true);
      setFormData(data);
      setIsLoading(false);
    } else if (!Object.keys(inputProps.errors).length && showPasswordFields) {
      setIsLoading(true);
      const finalData = { ...formData, ...data };
      const result = registerClient(finalData);
      setShowPasswordFields(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const phoneNumber = watch("phone");
  const email = watch("email");
  const companyName = watch("companyName");

  const debouncedEmail = useDebounce(email, 800);
  const debouncedPhone = useDebounce(phoneNumber, 800);
  const debouncedCompanyName = useDebounce(companyName, 800);

  const validateUser = async (changedFields = {}) => {
    let isValidPhone = true;
    let emailExists = false;
    let isValidCompanyName = true;

    if (changedFields.phone && debouncedPhone !== "") {
      isValidPhone = await validatePhone(debouncedPhone);
    }

    if (changedFields.email && debouncedEmail !== "") {
      emailExists = await findExistingCustomer({ type: "email", value: debouncedEmail });
    }

    if (changedFields.companyName && debouncedCompanyName !== "") {
      isValidCompanyName = await validateCompanyName(debouncedCompanyName);
      setIsValidCompanyName(isValidCompanyName);
    }

    if (!isValidPhone && emailExists) {
      setIsValidUser({
        show: true,
        text: `An account with the email address ${debouncedEmail} and/or phone number ${debouncedPhone} already exists. Please log in to that account or use a different email and phone number.`,
        titleText: "email address and/or phone number",
        buttonText: "email and phone",
      });
    } else if (!isValidPhone) {
      setIsValidUser({
        show: true,
        text: `An account with the number ${debouncedPhone} already exists. Please log in to that account or use a different number.`,
        titleText: "phone number",
        buttonText: "number",
      });
    } else if (emailExists) {
      setIsValidUser({
        show: true,
        text: `An account with the email address ${debouncedEmail} already exists. Please log in to that account or use a different email address.`,
        titleText: "email address",
        buttonText: "email address",
      });
    } else {
      setIsValidUser({ show: false, text: "", titleText: "", buttonText: "" });
    }
  };

  useEffect(() => {
    if (debouncedPhone !== "") {
      validateUser({ phone: true });
    }
  }, [debouncedPhone]);

  useEffect(() => {
    if (debouncedEmail !== "") {
      validateUser({ email: true });
    }
  }, [debouncedEmail]);

  useEffect(() => {
    if (debouncedCompanyName !== "") {
      validateUser({ companyName: true });
    }
  }, [debouncedCompanyName]);

  return (
    <div className={compClasses.root}>
      <form
        className={compClasses.registerForm}
        onSubmit={showPasswordFields ? registerPasswordSubmit(onRegisterSubmit) : registerSubmit(onRegisterSubmit)}
      >
        <div style={{ display: showPasswordFields ? "none" : "block" }}>
          <div className={compClasses.columnContainer}>
            <Typography className={compClasses.label}>Business Name</Typography>
            <InputField name="companyName" placeholder="E.g. Waste 123" {...inputProps} />
            {!isValidCompanyName && <Typography className={compClasses.errorText}>The company Name is already in use</Typography>}
          </div>
          <div className={compClasses.columnContainer}>
            <Typography className={compClasses.label}>Your full name</Typography>
            <InputField name="fullName" placeholder="E.g. Susan Jones" {...inputProps} />
          </div>
          <div className={compClasses.columnContainer}>
            <Typography className={compClasses.label}>Email</Typography>
            <InputField name="email" placeholder="example@email.com" {...inputProps} />
          </div>
          <div className={compClasses.columnContainer}>
            <Typography className={compClasses.label}>Phone number</Typography>
            <InputField fullWidth type="phone" name="phone" placeholder="E.g. (555) 555-1234" {...inputProps} />
          </div>
          <div className={compClasses.columnContainer}>
            <Typography className={compClasses.label}>Zip code and coverage area radius</Typography>
            <div className={compClasses.addressContainer}>
              <Controller
                render={({ onChange, value }) => {
                  return (
                    <AddressField
                      label=""
                      address={value}
                      setAddress={onChange}
                      {...inputProps}
                      placeholder={"E.g. 94088"}
                      InputProps={{ className: classes.fieldInput }}
                      types={["postal_code"]}
                      restrict={true}
                      debounce={300}
                      showLoadingMessage={true}
                      error={inputProps?.errors?.address?.message}
                      helperText={inputProps?.errors?.address?.message}
                      fullWidth
                    />
                  );
                }}
                name="address"
                control={inputProps.control}
              />
              <Controller
                render={({ onChange, value }) => {
                  return (
                    <Select variant="outlined" onChange={onChange} value={value} className={classes.textField}>
                      {Object.values(smsAlerts.distanceRange).map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  );
                }}
                name="distanceRange"
                control={inputProps.control}
              />
            </div>
          </div>
        </div>
        <div style={{ display: showPasswordFields ? "block" : "none" }}>
          <Typography className={`${compClasses.label}`}>Please provide a password to access your account later on</Typography>
          <div className={compClasses.columnContainer}>
            <InputField
              {...inputPasswordProps}
              className={compClasses.margin2}
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword}>
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <InputField
              {...inputPasswordProps}
              className={compClasses.margin2}
              name="confirmPassword"
              label="Confirm Password"
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword}>
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>
        <Box className={compClasses.alerts}>
          <Controller
            render={({ onChange, value }) => {
              return <Checkbox color="primary" value={value} onChange={(e) => onChange(e.target.checked)} />;
            }}
            name="alerts"
            control={inputProps.control}
          />

          <Typography className={compClasses.label}>I want to hear about more tasks like this one in the future</Typography>
        </Box>
        <div className={classes.columnContainer}>
          <Typography className={compClasses.linkOuter}>
            By accepting this task I agree to the{" "}
            <Link href="/terms" target="_blank" className={compClasses.link}>
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link href={`/service-provider-policy`} target="_blank" className={compClasses.link}>
              Service Provider Policy
            </Link>
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          type="submit"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          className={compClasses.button}
        >
          {isLoading ? "Saving" : "Save and Continue"}
        </Button>
      </form>
    </div>
  );
};

const GuestLoginModal = ({ open, setOpen, setLoginModalOpen, acceptTask = false }) => {
  const [accountExistsModal, setAccountExistsModal] = useState(false);
  const [isValidUser, setIsValidUser] = useState({ show: false, text: "", titleText: "", buttonText: "" });

  return (
    <>
      <Modal
        open={open}
        containerClass={useStyles().containerClass}
        setOpen={setOpen}
        title="Add your details to accept the task"
        header={
          <ModalContent
            setOpen={setOpen}
            setAccountExistsModal={setAccountExistsModal}
            isValidUser={isValidUser}
            setIsValidUser={setIsValidUser}
            acceptTask={acceptTask}
          />
        }
      />
      <AccountExistsModal
        open={accountExistsModal}
        setOpen={setAccountExistsModal}
        title={`An account with this ${isValidUser.titleText} already exists`}
        text={isValidUser.text}
        buttonText={isValidUser.buttonText}
        setLoginModalOpen={setLoginModalOpen}
        setGuestLoginModalOpen={setOpen}
      />
    </>
  );
};

export default GuestLoginModal;
